import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Car } from "lucide-react";
import { HelpIcon } from "@/components/HelpIcon";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const empty = { full_name: "", phone: "", email: "", unit_number: "", building: "" };

export default function Tenants() {
  const { activeProperty } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [vehicleCounts, setVehicleCounts] = useState({}); // tenant_id -> count
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  // Delete dialog state — `target` is the tenant; the dialog now shows a
  // confirmation that cascades vehicles when present, per spec.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    if (!activeProperty) return;
    setLoading(true);
    try {
      const [t, v] = await Promise.all([
        api.get(`/tenants?property_id=${activeProperty.id}`),
        api.get(`/vehicles?property_id=${activeProperty.id}`),
      ]);
      setRows(t.data || []);
      const counts = {};
      (v.data || []).forEach((veh) => {
        counts[veh.tenant_id] = (counts[veh.tenant_id] || 0) + 1;
      });
      setVehicleCounts(counts);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeProperty]);

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/tenants/${editing.id}`, form);
        toast.success("Tenant updated");
      } else {
        await api.post(`/tenants`, { ...form, property_id: activeProperty.id });
        toast.success("Tenant added");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // Cascade delete — confirm dialog has already warned the user about
      // attached vehicles. force=true tells the backend to delete vehicles too.
      const hasVehicles = (vehicleCounts[deleteTarget.id] || 0) > 0;
      const url = hasVehicles
        ? `/tenants/${deleteTarget.id}?force=true`
        : `/tenants/${deleteTarget.id}`;
      await api.delete(url);
      toast.success(
        hasVehicles
          ? `Tenant and ${vehicleCounts[deleteTarget.id]} vehicle(s) removed`
          : "Tenant removed"
      );
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono-tiny text-[#71717A]">Tenants</div>
          <div className="flex items-center gap-2 mt-2">
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              People ({rows.length})
            </h1>
            <HelpIcon step={1} label="Show tutorial · tenants & vehicles" />
          </div>
        </div>
        <Button
          onClick={() => nav("/dashboard/onboard")}
          className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-11"
          data-testid="add-tenant-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add tenant &amp; vehicle
        </Button>
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) {
              setEditing(null);
              setForm(empty);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">
                {editing ? "Edit tenant" : "Add tenant"}
              </DialogTitle>
              <DialogDescription>
                Tenants live at this property and can have multiple vehicles registered.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="mono-tiny text-[#71717A]">Full name</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="h-11"
                  data-testid="tenant-full-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="mono-tiny text-[#71717A]">Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="h-11"
                    data-testid="tenant-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="mono-tiny text-[#71717A]">Email (optional)</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="h-11"
                    data-testid="tenant-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="mono-tiny text-[#71717A]">Unit number</Label>
                  <Input
                    value={form.unit_number}
                    onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
                    className="h-11"
                    data-testid="tenant-unit"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="mono-tiny text-[#71717A]">Building</Label>
                  <Input
                    value={form.building}
                    onChange={(e) => setForm({ ...form, building: e.target.value })}
                    className="h-11"
                    data-testid="tenant-building"
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="tenant-cancel">
                Cancel
              </Button>
              <Button
                onClick={submit}
                className="bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                data-testid="tenant-save"
              >
                {editing ? "Save changes" : "Add tenant"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Full name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Building</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[#71717A] py-8">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[#71717A] py-8">
                  No tenants yet. Add your first tenant above.
                </TableCell>
              </TableRow>
            )}
            {rows.map((t) => (
              <TableRow key={t.id} data-testid={`tenant-row-${t.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {t.full_name}
                    {(vehicleCounts[t.id] || 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#0F8F4F]/10 text-[#0F8F4F] border border-[#0F8F4F]/20"
                        data-testid={`tenant-vehicle-count-${t.id}`}
                      >
                        <Car className="w-3 h-3" strokeWidth={2} />
                        {vehicleCounts[t.id]}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-[#71717A]">{t.phone}</TableCell>
                <TableCell className="text-[#71717A]">{t.email || "—"}</TableCell>
                <TableCell>{t.unit_number}</TableCell>
                <TableCell>{t.building || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => nav(`/dashboard/onboard?tenant=${t.id}`)}
                    data-testid={`tenant-add-vehicle-${t.id}`}
                    className="mr-1 h-8"
                  >
                    <Car className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Add vehicle
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => nav(`/dashboard/onboard?edit_tenant=${t.id}`)}
                    data-testid={`tenant-edit-${t.id}`}
                    title="Edit tenant + vehicles"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setDeleteTarget(t)}
                    data-testid={`tenant-delete-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-[#EF4444]" strokeWidth={1.5} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent data-testid="tenant-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget && (vehicleCounts[deleteTarget.id] || 0) > 0
                ? "Delete tenant and vehicles?"
                : "Delete tenant?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (vehicleCounts[deleteTarget.id] || 0) > 0 ? (
                <span data-testid="tenant-delete-cascade-msg">
                  This tenant has{" "}
                  <b className="text-[#09090B]">
                    {vehicleCounts[deleteTarget.id]} vehicle
                    {vehicleCounts[deleteTarget.id] > 1 ? "s" : ""}
                  </b>{" "}
                  attached. Deleting this tenant will also delete all attached vehicles and
                  deactivate their QR codes. This cannot be undone.
                </span>
              ) : (
                <>
                  This will permanently remove{" "}
                  <b className="text-[#09090B]">{deleteTarget?.full_name}</b> from this property.
                  This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tenant-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                remove();
              }}
              disabled={deleting}
              className="bg-[#EF4444] hover:bg-[#DC2626]"
              data-testid="tenant-delete-confirm"
            >
              {deleting
                ? "Deleting…"
                : deleteTarget && (vehicleCounts[deleteTarget.id] || 0) > 0
                ? "Delete Tenant + Vehicles"
                : "Delete tenant"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
